import { useEditor, useNode } from "@craftjs/core";
import React, { useState, useContext, useEffect } from "react";

import {
  ColorInput,
  SelectInput,
  UnitInput,
  ToggleInput,
  BoxShadowInput,
  PaddingInput,
  Panel,
  Tabs,
  TextInput,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  RichTextFormatting,
  IconInput,
  TabsV2,
  RangeNumberInput,
} from "../../input";
import { useMemo } from "react";
import { supportLibrary } from "../../block-supports";
import {
  changeTransition,
  isUpdatedFromDefault,
  getDirectoryInfoFromParents,
  getDirectoryFieldOptions,
} from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { remove_prefix } from "../../helpers";
import { GrigoraEditorContext } from "../../editor/context";

export const ButtonSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    query: { node },
  } = useEditor();

  const parents = node(id).ancestors(true);

  const { directoryTypes } = useContext(GrigoraDashboardContext) ?? {};
  const { pagesData, currentPage } = useContext(GrigoraEditorContext) ?? {};

  // Effect to auto-select directory source when appropriate
  useEffect(() => {
    if (props.dynamicLink && !props.dynamicLinkSrc) {
      const directoryInfo = getDirectoryInfoFromParents(parents, node);
      if (directoryInfo) {
        setProp((props) => {
          props.dynamicLinkSrc = "directory-listing";
          props.dynamicLinkSrcData = {
            directory_type_id: directoryInfo.directoryTypeId,
            directory_field_id: "current-listing",
          };
        });
      } else {
        // Check if current page is a directory listing page
        var currentPageData = pagesData?.[currentPage] ?? {};

        if (
          currentPageData?.type === "template" &&
          currentPageData?.template === "directory-template" &&
          currentPageData?.directory_id
        ) {
          var pageDirectoryId = currentPageData?.directory_id;

          var directoryType = directoryTypes?.find(
            (type) =>
              remove_prefix(type.sk, "directory-type-") === pageDirectoryId,
          );
          if (directoryType) {
            setProp((props) => {
              props.dynamicLinkSrc = "directory-listing";
              props.dynamicLinkSrcData = {
                directory_type_id: pageDirectoryId,
                directory_field_id: "current-listing",
              };
            });
          }
        }
      }
    }
  }, [props.dynamicLink]);

  // Get saved attributes.
  const {
    typoSize,
    typoSizeTablet,
    typoSizeMobile,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoTransform,
    typoStyle,
    typoDecoration,
    typoWeight,
    textColor = "#fff",
    hoverTextColor,
    width,
    widthTablet,
    widthMobile,
    height,
    heightTablet,
    heightMobile,
    link,
    display,
    openInNewTab,
    align,
    alignTablet,
    alignMobile,
    textAlign,
    textAlignTablet,
    textAlignMobile,
    padding = { top: "10px", bottom: "10px", left: "10px", right: "10px" },
    paddingTablet,
    paddingMobile,
    backgroundColor = "#055099",
    hoverBackgroundColor,
    border,
    hoverBorder,
    borderRadius,
    hoverBorderRadius,
    boxShadow,
    hoverBoxShadow,
    noFollow,
    noReferrer,
    icon,
    customIcon,
    hasCustomIcon = false,
    iconColor = "#fff",
    hoverIconColor = "#fff",
    iconSize,
    iconPosition = "left",
    verticalAlign = "center",
    dynamicLink = false,
    dynamicLinkSrc,
    dynamicLinkSrcData,
    transition,
  } = props;

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            resetValue={undefined}
            value={display}
            onChange={(val) => {
              setProp((props) => {
                props.display = val;
              });
            }}
            options={[
              {
                label: "Block",
                value: "block",
              },
              {
                label: "Inline",
                value: "inline",
              },
              {
                label: "Inline Block",
                value: "inline-block",
              },
            ]}
            label="Display"
          />
          <ToggleInput
            isResetVisible={false}
            label="Dynamic Link"
            value={dynamicLink}
            onChange={(val) => {
              setProp((props) => {
                props.dynamicLink = val;
              });
            }}
          />
          {dynamicLink ? (
            <>
              <SelectInput
                isResetVisible={false}
                label="Link Src"
                value={dynamicLinkSrc}
                onChange={(val) => {
                  setProp((props) => {
                    props.dynamicLinkSrc = val;
                  });
                }}
                options={[
                  {
                    label: "-",
                    value: "",
                  },
                  {
                    label: "Post Link",
                    value: "post-link",
                  },
                  {
                    label: "Directory Listing",
                    value: "directory-listing",
                  },
                ]}
              />
              {dynamicLinkSrc === "directory-listing" &&
                (directoryTypes?.length > 0 ? (
                  <>
                    <SelectInput
                      isResetVisible={false}
                      label="Directory"
                      value={dynamicLinkSrcData?.directory_type_id}
                      onChange={(val) => {
                        setProp((props) => {
                          props.dynamicLinkSrcData = {
                            ...(props.dynamicLinkSrcData ?? {}),
                            directory_type_id: val,
                          };
                        });
                      }}
                      options={[
                        {
                          label: "Select Directory",
                          value: "",
                        },
                        ...directoryTypes?.map((type) => ({
                          label: type.name,
                          value: remove_prefix(type.sk, "directory-type-"),
                        })),
                      ]}
                    />
                    {dynamicLinkSrcData?.directory_type_id && (
                      <SelectInput
                        isResetVisible={false}
                        label="Link Field"
                        value={dynamicLinkSrcData?.directory_field_id}
                        onChange={(val) => {
                          setProp((props) => {
                            props.dynamicLinkSrcData = {
                              ...(props.dynamicLinkSrcData ?? {}),
                              directory_field_id: val,
                            };
                          });
                        }}
                        options={getDirectoryFieldOptions(
                          directoryTypes,
                          dynamicLinkSrcData?.directory_type_id,
                          ["link"],
                          true,
                        )}
                      />
                    )}
                  </>
                ) : (
                  <>
                    <span className="text-sm text-gray-500">
                      No directory types found
                    </span>
                  </>
                ))}
            </>
          ) : (
            <>
              <TextInput
                minLength={INPUT_LIMITS["outbound-link"].minlength}
                maxLength={INPUT_LIMITS["outbound-link"].maxlength}
                resetValue={undefined}
                value={link}
                onChange={(val) => {
                  setProp((props) => {
                    props.link = val;
                  });
                }}
                label={"Link"}
              />
            </>
          )}

          <ToggleInput
            resetValue={undefined}
            label="Open in new tab"
            value={openInNewTab}
            onChange={(val) => {
              setProp((props) => {
                props.openInNewTab = val;
              });
            }}
          />
          <ToggleInput
            resetValue={undefined}
            label="No Follow"
            value={noFollow}
            onChange={(val) => {
              setProp((props) => {
                props.noFollow = val;
              });
            }}
          />
          <ToggleInput
            resetValue={undefined}
            label="No Referrer"
            value={noReferrer}
            onChange={(val) => {
              setProp((props) => {
                props.noReferrer = val;
              });
            }}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: icon },
            { key: hasCustomIcon, default: false },
            { key: customIcon },
            { key: iconSize },
            { key: iconPosition, default: "left" },
          ])}
          id={`block-setting-${id}-icon`}
          title={"Icon"}
          initialOpen={false}
        >
          <IconInput
            resetValue={undefined}
            activeIcon={icon}
            setActiveIcon={(val) => {
              setProp((props) => {
                props.icon = val;
              });
            }}
            supportCustom
            hasCustomIcon={hasCustomIcon}
            setHasCustomIcon={(val) => {
              setProp((props) => {
                props.hasCustomIcon = val;
              });
            }}
            customIcon={customIcon}
            setCustomIcon={(val) => {
              setProp((props) => {
                props.customIcon = val;
              });
            }}
          />
          {icon && (
            <>
              <UnitInput
                resetValue={undefined}
                value={iconSize}
                onChange={(val) => {
                  setProp((props) => {
                    props.iconSize = val;
                  });
                }}
                label={"Icon Size"}
              />
              <SelectInput
                resetValue={"left"}
                value={iconPosition}
                onChange={(val) => {
                  setProp((props) => {
                    props.iconPosition = val;
                  });
                }}
                options={[
                  {
                    label: "Left",
                    value: "left",
                  },
                  {
                    label: "Right",
                    value: "right",
                  },
                ]}
                label="Position"
              />
            </>
          )}
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: width },
            { key: widthTablet },
            { key: widthMobile },
            { key: height },
            { key: heightTablet },
            { key: heightMobile },
            { key: align },
            { key: alignTablet },
            { key: alignMobile },
            { key: alignMobile },
            { key: textAlign },
            { key: textAlignTablet },
            { key: textAlignMobile },
            { key: verticalAlign, default: "center" },
            {
              key: padding,
              default: {
                top: "10px",
                bottom: "10px",
                left: "10px",
                right: "10px",
              },
            },
            { key: paddingMobile },
            { key: paddingTablet },
          ])}
          id={`block-setting-${id}-dimension`}
          title={"Dimension"}
          initialOpen={true}
        >
          <UnitInput
            resetValue={undefined}
            value={width}
            onChange={(val) => {
              setProp((props) => {
                props.width = val;
              });
            }}
            label={"Width"}
            isResponsive={true}
            valueTablet={widthTablet}
            valueMobile={widthMobile}
            setValueTablet={(val) => {
              setProp((props) => {
                props.widthTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.widthMobile = val;
              });
            }}
          />
          <UnitInput
            resetValue={undefined}
            value={height}
            onChange={(val) => {
              setProp((props) => {
                props.height = val;
              });
            }}
            label={"Height"}
            isResponsive={true}
            valueTablet={heightTablet}
            valueMobile={heightMobile}
            setValueTablet={(val) => {
              setProp((props) => {
                props.heightTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.heightMobile = val;
              });
            }}
          />
          <AlignInput
            isResponsive={true}
            value={align}
            valueTablet={alignTablet}
            valueMobile={alignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.alignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.alignMobile = val;
              });
            }}
            label={"Align"}
          />
          <AlignInput
            isResponsive={true}
            resetValue={undefined}
            value={textAlign}
            valueTablet={textAlignTablet}
            valueMobile={textAlignMobile}
            onChange={(val) => {
              setProp((props) => {
                props.textAlign = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.textAlignTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.textAlignMobile = val;
              });
            }}
            label={"Text Align"}
          />
          <SelectInput
            resetValue={undefined}
            value={verticalAlign}
            onChange={(val) => {
              setProp((props) => {
                props.verticalAlign = val;
              });
            }}
            options={[
              { label: "Top", value: "top" },
              { label: "Center", value: "tocenterp" },
              { label: "Bottom", value: "bottom" },
            ]}
            label="Vertical Align"
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoSize },
            { key: typoSizeTablet },
            { key: typoSizeMobile },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoWeight },
            { key: typoTransform },
            { key: typoDecoration },
            { key: typoStyle },
          ])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={typoSize}
            valueTablet={typoSizeTablet}
            valueMobile={typoSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.typoSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.typoSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
        <RichTextFormatting />
      </div>
    );
  }

  function StyleSettings() {
    const colorsTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverSettings(),
      },
    ];
    function normalSettings() {
      return (
        <>
          {" "}
          {icon && (
            <ColorInput
              resetValue={textColor}
              value={iconColor}
              onChange={(val) => {
                setProp((props) => {
                  props.iconColor = val;
                });
              }}
              label={"Icon"}
            />
          )}
          <ColorInput
            resetValue={"#ffffff"}
            value={textColor}
            onChange={(val) => {
              setProp((props) => {
                props.textColor = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            value={backgroundColor}
            resetValue={"#055099"}
            onChange={(val) => {
              setProp((props) => {
                props.backgroundColor = val;
              });
            }}
            label={"Background"}
          />
        </>
      );
    }

    function hoverSettings() {
      return (
        <>
          {" "}
          {icon && (
            <ColorInput
              resetValue={hoverTextColor}
              value={hoverIconColor}
              onChange={(val) => {
                setProp((props) => {
                  props.hoverIconColor = val;
                });
              }}
              label={"Icon"}
            />
          )}
          <ColorInput
            resetValue={"#ffffff"}
            value={hoverTextColor}
            onChange={(val) => {
              setProp((props) => {
                props.hoverTextColor = val;
                props.hoverIconColor = val;
              });
            }}
            label={"Text"}
          />
          <ColorInput
            value={hoverBackgroundColor}
            resetValue={"#055099"}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBackgroundColor = val;
              });
            }}
            label={"Background"}
          />{" "}
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["color"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["color", "background-color"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: iconColor, default: "#fff" },
              { key: textColor, default: "#fff" },
              { key: backgroundColor, default: "#055099" },
              { key: hoverIconColor },
              { key: hoverTextColor },
              { key: hoverBackgroundColor },
            ])}
            id={`block-setting-${id}-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <TabsV2 items={colorsTabs} />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    const borderTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalBorderSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverBorderSettings(),
      },
    ];
    function normalBorderSettings() {
      return (
        <>
          <BorderInput
            resetValue={undefined}
            value={border}
            onChange={(val) => {
              setProp((props) => {
                props.border = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={borderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.borderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </>
      );
    }
    function hoverBorderSettings() {
      return (
        <>
          <BorderInput
            resetValue={undefined}
            value={hoverBorder}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBorder = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={undefined}
            value={hoverBorderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBorderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["border"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["border", "border-radius"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    const boxShadowTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalBoxShadowSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverBoxShadowSettings(),
      },
    ];
    function normalBoxShadowSettings() {
      return (
        <>
          {" "}
          <BoxShadowInput
            resetValue={undefined}
            value={boxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.boxShadow = val;
              });
            }}
          />
        </>
      );
    }
    function hoverBoxShadowSettings() {
      return (
        <>
          {" "}
          <BoxShadowInput
            resetValue={undefined}
            value={hoverBoxShadow}
            onChange={(val) => {
              setProp((props) => {
                props.hoverBoxShadow = val;
              });
            }}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.["box-shadow"]?.duration}
            onChange={(val) => {
              changeTransition(
                ["box-shadow"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: padding },
            { key: paddingTablet },
            { key: paddingMobile },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={false}
        >
          <PaddingInput
            resetValue={{
              top: "10px",
              bottom: "10px",
              left: "10px",
              right: "10px",
            }}
            label={"Padding"}
            isResponsive={true}
            valueTablet={paddingTablet}
            valueMobile={paddingMobile}
            value={padding}
            onChange={(val) => {
              setProp((props) => {
                props.padding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.paddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.paddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderRadius },
            { key: boxShadow },
            { key: hoverBorder },
            { key: hoverBorderRadius },
            { key: hoverBoxShadow },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={false}
        >
          <TabsV2 items={borderTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: boxShadow }])}
          id={`block-setting-${id}-box-shadow`}
          title={"Shadows"}
          initialOpen={false}
        >
          <TabsV2 items={boxShadowTabs} />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: display },
        { key: dynamicLink },
        { key: dynamicLinkSrc },
        { key: link },
        { key: openInNewTab },
        { key: noFollow },
        { key: noReferrer },
        { key: icon },
        { key: hasCustomIcon, default: false },
        { key: customIcon },
        { key: iconSize },
        { key: iconPosition, default: "left" },
        { key: width },
        { key: height },
        { key: align },
        { key: alignTablet },
        { key: alignMobile },
        { key: alignMobile },
        { key: textAlign },
        { key: textAlignTablet },
        { key: textAlignMobile },
        { key: verticalAlign, default: "center" },
        {
          key: padding,
          default: { top: "10px", bottom: "10px", left: "10px", right: "10px" },
        },
        { key: paddingMobile },
        { key: paddingTablet },
        { key: typoSize },
        { key: typoSizeTablet },
        { key: typoSizeMobile },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: iconColor, default: "#fff" },
        { key: textColor, default: "#fff" },
        { key: backgroundColor, default: "#055099" },
        { key: hoverIconColor },
        { key: hoverTextColor },
        { key: hoverBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: border },
        { key: borderRadius },
        { key: boxShadow },
        { key: hoverBorder },
        { key: hoverBorderRadius },
        { key: hoverBoxShadow },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
